import type { ApiAppConfig, ApiInitialArgs } from '../api/types';

// Telegram features Jaryan does not offer. Nothing behind them is deleted: each is switched off here, and
// taking a family out of DISABLED_FEATURES brings all of it back.
export type JaryanFeature = 'premium' | 'stars' | 'gifts' | 'ton' | 'business' | 'boosts' | 'sponsored' | 'ai'
  | 'communities' | 'httpTransport' | 'qrLogin' | 'translation' | 'profileMusic' | 'contactPhotoAndBirthday'
  | 'folderSharing' | 'channelSignatures' | 'greetingSticker';

const DISABLED_FEATURES: ReadonlySet<JaryanFeature> = new Set<JaryanFeature>([
  'premium',
  'stars',
  'gifts',
  'ton',
  'business',
  'boosts',
  'sponsored',
  'ai',
  'communities',
  'httpTransport',
  'qrLogin',
  'translation',
  'profileMusic',
  'contactPhotoAndBirthday',
  'folderSharing',
  // A channel's posts carry no author on Jaryan, so signing them, and showing the authors' profiles, would
  // change nothing
  'channelSignatures',
  // Hidden for now. An empty chat with a contact shows the plain empty state instead of the sticker to send
  'greetingSticker',
]);

// The actions each family is entered or loaded through. Every button, link and deep link reaches a feature
// through one of these, so a disabled family's actions do nothing and send nothing to the server.
export const FEATURE_ACTIONS: Record<JaryanFeature, readonly string[]> = {
  premium: [
    'openPremiumModal', 'loadPremiumGifts', 'sendPremiumGiftByStars', 'processPremiumFloodWait',
    'loadPremiumStickers',
    // Checklists need Premium to create
    'openTodoListModal',
  ],
  stars: [
    'openStarsBalanceModal', 'openStarsTransactionModal', 'openStarsSubscriptionModal', 'openStarsGiftModal',
    'openStarsGiftingPickerModal', 'openStarsTransactionFromGift', 'openPrizeStarsTransactionFromGiveaway',
    'openPaidReactionModal', 'sendPaidReaction', 'addLocalPaidReaction', 'loadStarStatus', 'loadStarsTransactions',
    'loadStarsSubscriptions', 'loadSendPaidReactionsAs', 'loadPaidReactionPrivacy', 'sendStarPaymentForm',
    'processOriginStarsPayment', 'fulfillStarsSubscription', 'changeStarsSubscription', 'updatePaidMessagesPrice',
    'toggleNoPaidMessagesException', 'setPaidMessageAutoApprove', 'openPaymentMessageConfirmDialogOpen',
  ],
  gifts: [
    'openGiftModal', 'openGiftRecipientPicker', 'openGiftInfoModal', 'openGiftInfoModalFromMessage',
    'openGiftUpgradeModal', 'openGiftTransferModal', 'openGiftTransferConfirmModal', 'openGiftWithdrawModal',
    'openGiftResalePriceComposerModal', 'openGiftInMarket', 'openGiftStatusInfoModal', 'openGiftInfoValueModal',
    'openGiftDescriptionRemoveModal', 'openLockedGiftModalInfo', 'openStarGiftPriceDecreaseInfoModal',
    'openGiftAuctionModal', 'openGiftAuctionBySlug', 'openGiftAuctionBidModal', 'openGiftAuctionInfoModal',
    'openGiftAuctionAcquiredModal', 'openGiftAuctionChangeRecipientModal', 'openUniqueGiftBySlug',
    'loadStarGifts', 'loadPeerSavedGifts', 'reloadPeerSavedGifts', 'loadMyUniqueGifts', 'loadResaleGifts',
    'loadStarGiftCollections', 'loadActiveGiftAuctions', 'buyStarGift', 'sendStarGift', 'sendStarGiftAuctionBid',
    'upgradeGift', 'upgradePrepaidGift', 'transferGift', 'convertGiftToStars', 'checkCanSendGift',
    'checkGiftCode', 'applyGiftCode',
    'loadGiftAuction', 'openActiveGiftAuctionsModal', 'openAboutStarGiftModal', 'openGiftPreviewModal',
    'openGiftOfferAcceptModal', 'acceptStarGiftOffer', 'declineStarGiftOffer', 'openGiftCraftModal',
    'openGiftCraftInfoModal', 'openGiftCraftSelectModal', 'craftStarGift', 'loadMoreCraftableGifts',
    'loadMoreMarketCraftableGifts', 'selectGiftForCraft', 'selectPurchasedGiftForCraft', 'updateStarGiftPrice',
    'processStarGiftWithdrawal', 'changeGiftVisibility', 'toggleSavedGiftPinned', 'removeGiftDescription',
    'shiftGiftUpgradeNextPrice', 'updateCraftGiftsFilter',
  ],
  ton: ['loadTonGifts', 'requestCollectibleInfo', 'loadUserCollectibleStatuses'],
  business: ['resolveBusinessChatLink'],
  boosts: [
    'openBoostModal', 'openBoostStatistics', 'applyBoost', 'loadMoreBoosters', 'processBoostParameters',
    'openGiveawayModal', 'launchPrepaidGiveaway', 'launchPrepaidStarsGiveaway', 'openMonetizationStatistics',
    'loadChannelMonetizationStatistics', 'openMonetizationVerificationModal',
    'processMonetizationRevenueWithdrawalUrl',
  ],
  sponsored: ['loadSponsoredMessages', 'viewSponsored', 'clickSponsored', 'reportSponsored', 'hideSponsored'],
  // Telegram's AI compose and its tones run on Telegram's servers; Jaryan's backend has none of these methods
  ai: [
    'openAiMessageEditorModal', 'openAiToneEditorModal', 'openAiTonePreview', 'loadAiComposeTones',
    'loadAiTonePreviewExample', 'createAiTone', 'saveAiTone', 'updateAiTone', 'deleteAiTone',
    'setChatTranslationTone', 'setMessageTranslationTone', 'composeWithAiMessageEditor',
  ],
  // Telegram's communities group channels under one entry; Jaryan's backend has no communities methods
  communities: ['loadCommunities', 'loadFullCommunity', 'openCommunityPanel', 'toggleCommunityCollapsed'],
  // Telegram's servers also answer over HTTP long polling, and the client falls back to it when a WebSocket
  // fails. Jaryan's gateway speaks WebSocket only. It is entered through a setting, not an action
  httpTransport: [],
  // Jaryan's server answers no QR login yet: the sign-in opens on the phone number, and the QR screen is entered
  // only through this action
  qrLogin: ['goToAuthQrCode'],
  // Jaryan's server answers translation empty. Its settings, menu items and modal are hidden through
  // `IS_TRANSLATION_SUPPORTED`
  translation: [
    'requestChatTranslation', 'togglePeerTranslations', 'requestMessageTranslation', 'translateMessages',
    'markMessagesTranslationPending', 'toggleAutoTranslation',
  ],
  // Jaryan's server does not keep music on profiles yet. Without the saved list the player hides its button
  profileMusic: ['toggleMusicInProfile', 'loadSavedMusicIds', 'loadSavedMusic'],
  // Jaryan's server has no method to set or suggest a contact's photo or to suggest a birthday. The photo upload
  // action stays registered: a bot's owner changes the bot's avatar through it
  contactPhotoAndBirthday: ['suggestBirthday'],
  // Hidden for now. A folder link that arrives is answered in `checkChatlistInvite`, and a folder already joined can
  // still be left
  folderSharing: [
    'loadChatlistInvites', 'openShareChatFolderModal', 'createChatlistInvite', 'editChatlistInvite',
    'deleteChatlistInvite', 'joinChatlistInvite',
  ],
  channelSignatures: ['toggleSignatures'],
  greetingSticker: ['loadGreetingStickers'],
};

const DISABLED_ACTIONS: ReadonlySet<string> = new Set(
  [...DISABLED_FEATURES].flatMap((feature) => FEATURE_ACTIONS[feature]),
);

// A request to join is never kept by Jaryan's server, so a channel with approval switched on admits nobody the admin
// can see. The switch is hidden in the chats listed here unless it is already on, so an admin can turn it off
const JOIN_REQUEST_SWITCH_HIDDEN_IN: ReadonlySet<'channel' | 'group'> = new Set(['channel', 'group']);

export function isJoinRequestSwitchShown(isChannel: boolean, isJoinRequestOn: boolean | undefined) {
  if (!JOIN_REQUEST_SWITCH_HIDDEN_IN.has(isChannel ? 'channel' : 'group')) return true;

  return Boolean(isJoinRequestOn);
}

// The guard bot approves the same join requests, which the server never keeps. Its switch on an admin's rights
// screen is gone unless the bot is already set to approve them, so an admin can turn it off
export function isGuardBotSwitchShown(isGuardBotSetOn: boolean) {
  return isGuardBotSetOn;
}

// The server ignores `request_needed` on an invite link and admits the user at once, so the link's approval switch
// would only mislead
export function isInviteApprovalSwitchShown() {
  return false;
}

export function isFeatureEnabled(feature: JaryanFeature) {
  return !DISABLED_FEATURES.has(feature);
}

export function isActionDisabled(name: string) {
  return DISABLED_ACTIONS.has(name);
}

// The flags upstream already honours to hide these features, set to match the list above
export function applyFeatureSwitches(appConfig: ApiAppConfig): ApiAppConfig {
  return {
    ...appConfig,
    isPremiumPurchaseBlocked: isFeatureEnabled('premium') ? appConfig.isPremiumPurchaseBlocked : true,
    isGiveawayGiftsPurchaseAvailable: isFeatureEnabled('boosts') ? appConfig.isGiveawayGiftsPurchaseAvailable : false,
    isStarsGiftEnabled: isFeatureEnabled('gifts') ? appConfig.isStarsGiftEnabled : false,
    starsPaidMessagesAvailable: isFeatureEnabled('stars') ? appConfig.starsPaidMessagesAvailable : false,
    isChannelRevenueWithdrawalEnabled: isFeatureEnabled('boosts') ? appConfig.isChannelRevenueWithdrawalEnabled : false,
  };
}

// The transport a profile has stored is not honoured while the HTTP transport is off: the stored default allows it
export function applyTransportSwitches(initialArgs: ApiInitialArgs): ApiInitialArgs {
  if (isFeatureEnabled('httpTransport')) return initialArgs;

  return { ...initialArgs, shouldAllowHttpTransport: false, shouldForceHttpTransport: false };
}
